import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WaterfallsSection from './components/WaterfallsSection';
import HikingSection from './components/HikingSection';
import DowntownSection from './components/DowntownSection';
import RestaurantsSection from './components/RestaurantsSection';
import SeasonalGuide from './components/SeasonalGuide';
import WhereToStaySection from './components/WhereToStaySection';
import FAQSection from './components/FAQSection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${siteUrl}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Things to Do in Highlands NC',
      item: `${siteUrl}/things-to-do`,
    },
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is there to do in Highlands NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Highlands offers waterfalls, hiking trails, scenic drives, shopping, and dining, making it a popular mountain destination.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are the best waterfalls in Highlands NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Popular waterfalls include Glen Falls, Dry Falls, and Bridal Veil Falls.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Highlands NC a good place to visit?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Highlands is known for its natural beauty, cool mountain climate, and relaxing atmosphere, making it a great place to visit year-round.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where should I stay in Highlands NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Visitors can choose from cabins, vacation rentals, and other accommodations located near trails, waterfalls, and downtown Highlands.',
      },
    },
    {
      '@type': 'Question',
      name: 'When is the best time to visit Highlands NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Highlands is a year-round destination, with cool summers, fall foliage in October, spring waterfalls, and a quieter winter season.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are there cabins near waterfalls in Highlands NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, many cabins in Highlands NC are located near waterfalls and hiking trails, offering easy access to outdoor activities.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Highlands NC good for hiking?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Highlands is known for its hiking trails, including Whiteside Mountain, Glen Falls Trail, and other scenic routes in the area.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are the best things to do in Highlands, NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Highlands, NC is known for its waterfalls, hiking trails, and scenic mountain views. Popular activities include visiting Glen Falls, exploring Dry Falls, hiking Whiteside Mountain, and enjoying the downtown area.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Glen Falls worth visiting in Highlands, NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Glen Falls is one of the most beautiful waterfalls in Highlands, NC. It features multiple cascades and a scenic hiking trail, making it a must-visit destination for visitors.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where should I stay near Glen Falls in Highlands, NC?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Many visitors choose to stay near Glen Falls for convenience and scenery. Glen Falls Luxury Cabin in Highlands, NC is a popular option due to its location, privacy, and high guest ratings.',
      },
    },
  ],
};

const WEBPAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Things to Do in Highlands NC',
  description:
    'A local guide to things to do in Highlands, North Carolina, including waterfalls, hiking trails, restaurants, and places to stay.',
  url: `${siteUrl}/things-to-do`,
  inLanguage: 'en-US',
  about: {
    '@type': 'Place',
    name: 'Highlands, North Carolina',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Highlands',
      addressRegion: 'NC',
      addressCountry: 'US',
    },
  },
};

export default function ThingsToDo() {
  return (
    <>
      <title>Things to Do in Highlands NC | Waterfalls, Hiking &amp; More — Highlands, North Carolina</title>
      <meta name="description" content="Discover the best things to do in Highlands, NC — waterfalls like Glen Falls and Dry Falls, hiking trails, restaurants, and seasonal activities in the Blue Ridge Mountains." />
      <meta name="keywords" content="things to do in Highlands NC, Highlands North Carolina activities, waterfalls Highlands NC, hiking Highlands NC" />
      <meta property="og:title" content="Things to Do in Highlands NC | Waterfalls, Hiking &amp; More" />
      <meta property="og:description" content="A local guide to things to do in Highlands, North Carolina — waterfalls, hiking trails, restaurants, and outdoor experiences in the Blue Ridge Mountains." />
      <meta name="robots" content="index, follow" />
      <meta name="geo.region" content="US-NC" />
      <meta name="geo.placename" content="Highlands, North Carolina" />
      <meta name="geo.position" content="35.0499;-83.1985" />
      <link rel="canonical" href={`${siteUrl}/things-to-do`} />
      <script type="application/ld+json">{JSON.stringify(WEBPAGE_SCHEMA)}</script>
      <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      <script type="application/ld+json">{JSON.stringify(BREADCRUMB_SCHEMA)}</script>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[70vh] min-h-[560px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/d28d66203f5f092c88f554967d7b0d7a.jpg"
            alt="Things to do in Highlands North Carolina — scenic mountain views and outdoor activities in the Blue Ridge Mountains"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-6 pt-20">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost font-light mb-4">/ Local Guide</p>
            <h1 className="font-cormorant text-5xl md:text-6xl text-white font-light leading-tight mb-5 max-w-4xl mx-auto">
              Things to Do in Highlands NC
            </h1>
            <p className="text-white/75 font-jost text-base leading-relaxed max-w-2xl">
              Explore waterfalls, hiking trails, scenic drives, restaurants, and outdoor experiences in Highlands, North Carolina — one of the most popular destinations in the Blue Ridge Mountains.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto">
            <p className="font-cormorant text-3xl md:text-4xl text-[#1A1A18] font-light leading-relaxed italic text-center mb-8">
              "Highlands is not a destination you rush through. It is a place you settle into — and find yourself reluctant to leave."
            </p>
            <div className="w-12 h-px bg-[#C9A96E] mx-auto mb-12" />
            <div className="text-left space-y-5">
              <p className="text-[#5C5850] font-jost text-base leading-relaxed">
                Sitting at 4,118 feet in the Southern Appalachians, Highlands, North Carolina is a mountain destination
                known for its breathtaking waterfalls, scenic hiking trails, charming downtown, and genuinely cool
                summer climate. Whether you're planning a weekend trip or a longer visit, the area offers a natural mix
                of outdoor beauty and small-town character that's hard to find elsewhere.
              </p>
              <p className="text-[#5C5850] font-jost text-base leading-relaxed">
                The surrounding landscape is rich with options for outdoor exploration — from accessible waterfall walks
                to more challenging summit hikes — alongside a walkable downtown with independent shops, galleries, and
                a well-regarded dining scene.
              </p>
              <p className="text-[#5C5850] font-jost text-base leading-relaxed">
                Many visitors choose to stay in{' '}
                <Link
                  to="/best-luxury-cabins"
                  className="text-[#2D4A3E] font-medium underline underline-offset-2 hover:text-[#1E3529] transition-colors"
                >
                  cabins in Highlands NC
                </Link>{' '}
                to enjoy close access to waterfalls, hiking trails, and the surrounding mountain landscape — with Glen Falls Luxury Cabin in Highlands, NC being one of the most sought-after options near the falls. This guide
                highlights some of the best things to do in Highlands, NC, including outdoor activities and{' '}
                <Link
                  to="/dog-friendly-highlands-nc"
                  className="text-[#2D4A3E] font-medium underline underline-offset-2 hover:text-[#1E3529] transition-colors"
                >
                  dog-friendly Highlands NC
                </Link>{' '}
                experiences for visitors traveling with pets.
              </p>
            </div>
          </div>
        </section>

        {/* H2: Waterfalls in Highlands NC */}
        <WaterfallsSection />

        {/* H2: Hiking Trails */}
        <HikingSection />

        {/* H2: Downtown Highlands */}
        <DowntownSection />

        {/* H2: Restaurants in Highlands NC */}
        <RestaurantsSection />

        {/* H2: Seasonal Things to Do */}
        <SeasonalGuide />

        {/* H2: Where to Stay in Highlands NC */}
        <WhereToStaySection />

        {/* H2: FAQ */}
        <FAQSection />

        {/* Wrap-up paragraph */}
        <section className="bg-[#FAFAF8] py-16 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#5C5850] font-jost text-base leading-relaxed">
              From waterfalls and hiking trails to downtown experiences and scenic mountain views, Highlands, North Carolina offers a wide range of things to do for visitors year-round. Whether you&apos;re planning a short getaway or a longer stay, consider making Glen Falls Luxury Cabin in Highlands, NC your base — a peaceful retreat within easy reach of the area&apos;s best outdoor attractions and experiences.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-[#2D4A3E] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-5">/ Plan Your Visit</p>
            <h2 className="font-cormorant text-5xl md:text-6xl text-white font-light mb-5">
              Ready to Explore <em className="italic">Highlands?</em>
            </h2>
            <p className="text-white/65 font-jost text-base leading-relaxed mb-10 max-w-lg mx-auto">
              Find the right place to stay and make the most of your time in Highlands, NC.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/best-luxury-cabins"
                className="inline-flex items-center justify-center gap-3 bg-[#C9A96E] hover:bg-[#B8954F] text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                View Cabins in Highlands NC
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
              <a
                href="https://www.airbnb.com/rooms/900012473285450950"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-5 rounded-full transition-colors cursor-pointer whitespace-nowrap border border-white/25"
              >
                View Glen Falls Cabin
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line" />
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
